import React, { useCallback, useEffect, useRef } from 'react';
import { useSetState } from 'ahooks';
import { Box, Button, Field, Message, Table, NumberPicker, Dialog } from '@fpxfd/next';
import $i18n from 'panda-i18n';

import FpxPage from '@/components/FpxPage';
import PageHeader from '@/components/PageHeader';
import FpxTable from '@/components/FpxTable';
import FpxDetail from '@/components/FpxDetail';
import VasMatchInventory from './VasMatchInventory';

import { Props, StatesProps, generalProps } from './index.interface';
import { getLogData, getVasInventoryApi, queryRelativeNoApi, submitData } from './index.services';
import styles from './index.module.scss';
import { transformToArray, transformToString, vToLabel } from '@/utils';
import FpxTab from '@/components/FpxTab';
import ActionArea from '@/components/ActionArea';
import { GENERAL_TAB, LABEL_TEXT, VAS_DEAL_TYPE, VAS_OPERATE_LOG } from '@/const';
import dealFileUrl from '@/utils/dealFileUrl';
import { useLocation } from 'ice';
import {
  basicItems,
  RepackageColumn,
  PhotoColumn,
  LabelColumn,
  TakeStockColumn,
  CheckColumn,
  VerifyVolumeColumn,
  UnpackingShelvingColumn,
  TanksColumn,
  ChangeLabelColumn,
  photoCell,
  labelCell,
  checkCell,
  UnpackingShelvingCell,
  TanksCell,
  unSubmitType,
} from './datas';
import FillPartCom from './FillPartCom';
import FpxTable2 from '@/components/FpxTable2';
import FpxDetailNew from '@/components/FpxDetailNew';
import AssociatedOrderCom from '@/pages/VASOrderDetail/component/AssociatedOrderCom';
import { trackVASSubmitSuccess, trackVASSubmitError } from '@/utils/tracker';

const VASDetail = ({ consignmentNo, allDatas, callback }: Props) => {
  const { pathname } = useLocation();
  const uptRef: any = useRef();
  const [states, setStates] = useSetState<StatesProps>({
    basicObj: {}, // 基本信息
    vasList: [], // 增值服务数据
    operationList: [],
    customerAttachmentUrl: [], // 客户附件数据
    warehouseAttachmentUrl: [], // 结果附件数据
    generalList: [], // 综合信息数据
    logList: [], // 操作日志数据
    tabKey: 'res',
    fileUrlList: [],
    firstSet: true,
    cellArr: [],
    status: '', // 单状态
    inboundOrder: '',
    inboundBatchNumber: '', // 入库批次号
    mustUpload: false, // 是否必须上传文件
    remarkVisible: false, // 备注visible
    submitLoading: false,
    typeList: [], // 增值预报信息各type的list集合，用于标签处理类型展示输入框
    totalNumberList: {},
    relativeData: {},
    vasInventoryList: [],
  });

  // 获取数据
  const field = Field.useField();
  // 页签操作
  const gettab = (e: string) => {
    setStates({
      tabKey: e,
    });
  };

  /**
   * 获取所有数据
   */
  const getAllData = useCallback(
    (allDatas) => {
      /**
       * 操作结果中如果有处理类型是MFP（拍照）的就必须上传文件
       */
      const mustUpload = allDatas?.valueAddedTaskSkuDTOList.filter((e) => e?.operationCode === 'MFP').length;
      /**
       * 数据初始换问题处理
       * 商品重新包装/拍照/清点库存/商品核实/核实材积重量
       */
      const shouldEditList = ['RP', 'PG', 'CS', 'VG', 'VVW'];
      let vasList = allDatas?.valueAddedTaskSkuDTOList || [];
      if (shouldEditList.includes(states.basicObj?.serviceType) && allDatas?.valueAddedTaskDTO?.status === 'P') {
        vasList =
          allDatas?.valueAddedTaskSkuDTOList?.map((_v) => {
            const _item = { ..._v };
            if (states.basicObj?.serviceType === 'VVW') {
              // 核实材积重量
              _item.actualLength = _item?.length;
              _item.actualWidth = _item?.width;
              _item.actualHeight = _item?.height;
              _item.actualWeight = _item?.weight;
            } else {
              _item.actualOperationQty = _item?.operationQty;
            }
            return _item;
          }) || [];
      }
      /**
       * 标签处理数据源单独处理
       */

      if (allDatas?.valueAddedTaskDTO?.serviceType === 'HL') {
        vasList = allDatas?.valueAddedTaskSkuDTOList?.length
          ? allDatas?.valueAddedTaskSkuDTOList
          : allDatas?.valueAddedTaskPackageDTOList || [];
      }

      /**
       * typeList
       */
      const typeList: string[] = [];
      vasList?.map((t) => {
        if (!typeList?.includes(t?.operationCode)) {
          typeList.push(t?.operationCode);
        }
      });

      /**
       * valueAddedTaskOperationDetailDTOList
       */
      const totalNumberList = {};
      allDatas?.valueAddedTaskOperationDetailDTOList?.forEach((e) => {
        totalNumberList[e?.operationCode] = e?.actualOperationQty;
      });

      setStates({
        mustUpload,
        basicObj: allDatas?.valueAddedTaskDTO, // 基本信息
        vasList, // 增值服务数据
        customerAttachmentUrl: transformToArray(allDatas?.valueAddedTaskDTO?.customerAttachmentUrl) || [], // 客户附件数据
        warehouseAttachmentUrl: transformToArray(allDatas?.valueAddedTaskDTO?.warehouseAttachmentUrl) || [],
        generalList: allDatas?.valueAddedTaskSkuDTOList || [], // 综合信息数据
        status: allDatas?.valueAddedTaskDTO?.status, // 单状态
        inboundOrder: allDatas?.valueAddedTaskDTO?.inboundOrder,
        inboundBatchNumber:
          allDatas?.valueAddedTaskSkuDTOList.length && allDatas?.valueAddedTaskSkuDTOList[0].inboundBatchNumber,
        typeList,
        totalNumberList,
        operationList: allDatas?.valueAddedTaskOperationDetailDTOList || [],
      });

      // 获取操作日志信息
      getLogData({ consignmentNo }).then((res) => {
        setStates({
          logList: res,
        });
      });
    },
    [consignmentNo, setStates, states.basicObj?.serviceType],
  );

  /**
   * 获取填写部分数据
   */
  const getFillDataFn = (fileUrlList: string[]) => {
    setStates({
      fileUrlList,
    });
  };

  /**
   * 提交
   */
  const submitAll = () => {
    field.validate((errors, values: any) => {
      const _form = { ...values };
      if (errors) return;
      if (states.mustUpload && !states.fileUrlList?.length) {
        return Message.notice($i18n.get({ id: 'PleaseUploadTheFileFirst.four_px', dm: '请先上传文件！' }));
      }
      // 验证文件URL是否有效
      if (states.fileUrlList?.length) {
        const invalidFile = states.fileUrlList.find((fileStr) => {
          const url = fileStr?.split('^')[0];
          return url === 'undefined' || url === 'null' || !url;
        });
        if (invalidFile) {
          const fileName = invalidFile?.split('^')[1] || 'unknown file';
          return Message.error(
            `${$i18n.get({
              id: 'UnableToUploadTheFilePlease_1010940675.four_px',
              dm: '文件上传失败，请重新上传！',
            })}: ${fileName}`,
          );
        }
      }
      setStates({
        submitLoading: true,
      });

      /**
       * 四种情况：
       * 1. 总数量：直接把填写的总数量赋值上去
       * 2. table填写单个操作数量，将所有数量加起来 （商品重新包装/拍照/清点库存/商品核实）
       * 3. 拆箱"UP"把零售SKU数量actualOperationQty相加、合箱"BP"把整箱SKU数量actualOperationQty相加，不用管类型
       * 4. 非上面几种，且页面上没有改数量的地方，直接actualOperationQty = operationQty;
       */

      let _operationList: any[] = [...states.operationList];
      /**
       * 预报信息数量的数据处理
       */
      let _vasList: any[] = [...states.vasList];
      if (
        states.basicObj?.serviceType === 'VVW' ||
        (states.basicObj?.serviceType === 'HL' && states.basicObj?.operationSegment === 'OB')
      ) {
        // 核实材积重量/ 标签处理 --- 出库段
        _vasList = _vasList?.map((e) => ({
          ...e,
          actualOperationQty: _form[`${e?.operationCode}`] || 0,
        }));
      } else if (states.basicObj?.serviceType === 'HL') {
        // 标签处理 --- 非出库段
        _vasList = _vasList?.map((e) => ({
          ...e,
          actualOperationQty: e?.operationQty || 0,
        }));
      }
      const _delArr = VAS_DEAL_TYPE?.map((e) => e?.value);
      _delArr.map((e) => {
        if (_form[`${e?.value}`]) delete _form[`${e?.value}`];
      });

      // 3.table填写单个操作数量，将所有数量加起来，HL上面判断了
      const shouldDealList = ['RP', 'PG', 'CS', 'VG', 'HL'];

      if (
        states.basicObj?.serviceType === 'VVW' ||
        (states.basicObj?.serviceType === 'HL' && states.basicObj?.operationSegment === 'OB')
      ) {
        // 1. 总金额
        _operationList = _operationList.map((e) => ({
          ...e,
          actualOperationQty: _form[`${e?.operationCode}`] || 0,
        }));
      } else if (shouldDealList?.includes(states.basicObj?.serviceType)) {
        // 2.table填写单个操作数量，将所有数量加起来，HL上面判断了
        _operationList = _operationList.map((e) => {
          const _item = { ...e, actualOperationQty: 0 };
          _vasList?.forEach((_v) => {
            if (_v?.operationCode === e?.operationCode) {
              _item.actualOperationQty += _v?.actualOperationQty;
            }
          });
          return _item;
        });
      } else if (states.basicObj?.serviceType === 'UP') {
        /**
         * 3. 拆箱"UP"
         * UB的单独处理，如果UB的actualOperationQty!=0，就直接取原来的值，否则就把_vasList的所有actualOperationQty相加，其余类型的不处理
         * 对所有的非UB类型进行拆箱操作，然后汇总，当做UB总数传给后端
         */
        const _newList: any[] = [];
        _vasList.forEach((e) => {
          _newList.push(e);
          if (e?.operationCode !== 'UB') {
            _newList.push({
              ...e,
              operationCode: 'UB',
            });
          }
        });
        console.log('new', _newList);

        _operationList = _operationList.map((e) => {
          const _item = { ...e, actualOperationQty: 0 };
          _newList.forEach((_v) => {
            if (_v?.operationCode === e?.operationCode) {
              _item.actualOperationQty += _v?.actualOperationQty;
            }
          });
          return _item;
        });
      } else if (states.basicObj?.serviceType === 'BP') {
        /**
         * 合箱"BP"
         * 先过滤数据，operationCode相同且actualSkuCode的数据只保留一条
         * 然后根据operationCode相同sum数据
         */
        const _filterList: any[] = [];
        (_vasList || []).forEach((e) => {
          const _flag = _filterList?.some(
            (f) => f?.operationCode === e?.operationCode && f?.actualSkuCode === e?.actualSkuCode,
          ); // true已存在
          !_flag && _filterList.push(e);
        });
        _operationList = _operationList.map((e) => {
          const _item = { ...e, actualOperationQty: 0 };
          (_filterList || []).forEach((_v) => {
            if (_v?.operationCode === e?.operationCode) {
              _item.actualOperationQty += _v?.actualOperationQty;
            }
          });
          return _item;
        });
      } else {
        // 4. 非上面几种，且页面上没有改数量的地方，直接actualOperationQty = operationQty;
        _operationList = _operationList?.map((e) => ({
          ...e,
          actualOperationQty: e?.operationQty,
        }));
      }

      console.log(_vasList);
      console.log(_operationList);

      const req = {
        ..._form,
        consignmentNo,
        operationDetailDTOList: _operationList,
        valueAddedTaskSkuDTOList:
          states.basicObj?.serviceType === 'HL' && states.basicObj?.operationSegment !== 'OB'
            ? _vasList
            : states.vasList,
        warehouseAttachmentUrl: transformToString(states.fileUrlList),
      };

      submitData(req)
        .then((res) => {
          // 提交成功埋点
          trackVASSubmitSuccess(consignmentNo, req, res);

          setStates({
            submitLoading: false,
          });
          Message.success($i18n.get({ id: 'TJCG！.SubmittedSuccessfully.four_px', dm: '提交成功！' }));
          callback();
        })
        .catch((error) => {
          // 提交失败埋点
          trackVASSubmitError(consignmentNo, req, error);

          setStates({
            submitLoading: false,
          });
        });
    });
  };

  /**
   * 获取数量
   * 贴超重标签 Paste overweight label
      贴箱唛 Paste box mark
      贴托唛 Paste pallet mark
      贴面单 Paste waybill
      贴装箱单 Paste packing list
      贴欧规安全标签 Paste Europe label
      贴FBA商品标签 Paste FBA Product Label
   */
  const getHLCom = useCallback(() => {
    return Object.keys(states.totalNumberList)?.map((key) => {
      return (
        <span>
          {vToLabel(LABEL_TEXT, key)}：{states.totalNumberList[key]}
        </span>
      );
    });
  }, [states.totalNumberList]);

  /**
   * 增值服务预报信息Columns
   */
  const getForecastColumns = useCallback(() => {
    switch (states.basicObj?.serviceType) {
      case 'RP':
        return RepackageColumn; // 商品重新包装
      case 'PG':
        return PhotoColumn; // 拍照
      case 'HL':
        return LabelColumn; // 标签处理
      case 'CS':
        return TakeStockColumn; // 清点库存
      case 'VG':
        return CheckColumn; // 商品核实
      case 'VVW':
        return VerifyVolumeColumn; // 核实材积重量
      case 'UP':
        return UnpackingShelvingColumn; // 拆箱上架
      case 'BP':
        return TanksColumn; // 合箱上架
      case 'OTOC':
        return ChangeLabelColumn; // 一对一换标
      default:
        return RepackageColumn;
    }
  }, [states.basicObj]);

  /**
   * 需要合并的单元格dataIndex
   */
  const getMergeCell = useCallback(() => {
    switch (states.basicObj?.serviceType) {
      case 'PG':
        return photoCell; // 拍照
      case 'HL':
        return labelCell; // 标签处理
      case 'VG':
        return checkCell; // 商品核实
      case 'UP':
        return UnpackingShelvingCell; // 拆箱上架
      case 'BP':
        return TanksCell; // 合箱上架
      default:
        return [];
    }
  }, [states.basicObj]);

  /**
   * 修改列表值
   */
  const dealList = useCallback(
    (value, type: string, index: number) => {
      const _list: [] = [...states.vasList];
      _list[index][type] = value;
      setStates({
        vasList: _list,
      });
    },
    [setStates, states.vasList],
  );

  const dealCell = useCallback(
    (dataSource: []) => {
      const arr = [...dataSource];
      const result: any = [];
      let index = 0;

      while (index < arr.length) {
        const m: generalProps = arr[index];
        const len = arr.filter((e: generalProps, i: number) => {
          const cell = getMergeCell();
          const flag = cell.filter((c) => {
            return c !== 'No' && e[c] && m[c] && e[c] !== m[c];
          })?.length;
          if (!flag) {
            arr[i].No = result?.length + 1;
            return true;
          }
          return false;
        }).length;
        if (len > 1) result.push([index, len]);
        index += len || 1;
      }

      setStates({
        firstSet: false,
        cellArr: result,
      });
    },
    [getMergeCell, setStates],
  );

  const dealCellProps = useCallback(
    (rowIndex: number, colIndex: number, dataIndex: string, record: any) => {
      const arr = getMergeCell();
      for (let i = 0; i < states.cellArr.length; i++) {
        if (rowIndex === states.cellArr[i][0] && arr.includes(dataIndex)) {
          return {
            rowSpan: states.cellArr[i][1],
          };
        }
      }
    },
    [getMergeCell, states.cellArr],
  );

  useEffect(() => {
    getAllData(allDatas);
  }, [allDatas, getAllData]);

  useEffect(() => {
    if (states.firstSet && states.vasList.length) {
      dealCell(states.vasList);
    }
  }, [states.vasList, states.firstSet, dealCell]);

  const handleUptBtn = () => {
    uptRef.current.changeApVisible(true);
  };

  const uptCallBack = () => {
    queryRelativeNo();
  };
  const queryRelativeNo = () => {
    queryRelativeNoApi({ consignmentNo }).then((res) => {
      setStates({ relativeData: res || {} });
    });
  };
  useEffect(() => {
    if (consignmentNo) {
      queryRelativeNo();
    }
  }, [consignmentNo]);
  return (
    <>
      {/* 基本信息、增值服务预报信息 */}
      <FpxPage
        header={
          <PageHeader
            title={$i18n.get({ id: 'ValueAddedOrders.four_px', dm: '增值订单' })}
            breadcrumbs={[
              { path: '', name: $i18n.get({ id: 'Order.four_px', dm: '订单' }) },
              { path: '/order/vasorder', name: $i18n.get({ id: 'ValueAddedOrders.four_px', dm: '增值订单' }) },
              {
                path: pathname,
                name: $i18n.get({ id: 'ValueAddedOrderDetails.four_px', dm: '增值订单详情' }),
              },
            ]} // 子状态（待匹配MP/待拣选PP/待换表CL/待上架PA）的不应该显示提交按钮
            btnList={
              states.status === 'P' && !unSubmitType?.includes(states.basicObj?.subStatus)
                ? [
                    {
                      name: `${$i18n.get({ id: 'Submitted.four_px', dm: '提交' })}`,
                      callback: submitAll,
                      loading: states.submitLoading,
                    },
                  ]
                : []
            }
          />
        }
        detail={
          <FpxDetailNew
            title={$i18n.get({ id: 'BasicInformation.four_px', dm: '基本信息' })}
            items={basicItems}
            dataSource={states.basicObj}
          >
            <div slot="associatedOrder">
              <Box direction="row" align="center">
                {states.relativeData?.allNos?.join(',')}
                <Button
                  text
                  type="primary"
                  style={{ marginLeft: 10 }}
                  onClick={() => {
                    handleUptBtn();
                  }}
                >
                  {$i18n.get({ id: 'Modify.four_px', dm: '修改' })}
                </Button>
              </Box>
            </div>

            <div slot="vasMatchInventory">
              <Button
                text
                type="primary"
                style={{ marginLeft: 10 }}
                onClick={() => {
                  getVasInventoryApi(states.basicObj?.consignmentNo).then((res) => {
                    setStates({ visible: true, vasInventoryList: res });
                  });
                }}
              >
                {$i18n.get({ id: 'See.four_px', dm: '查看' })}
              </Button>
            </div>
          </FpxDetailNew>
        }
        tableTitle={$i18n.get({ id: 'ValueAddedServiceForecastInformation.four_px', dm: '增值服务预报信息' })}
        table={
          <>
            {states.status === 'P' ? (
              <FillPartCom
                field={field}
                callBack={getFillDataFn}
                type={states.basicObj?.serviceType}
                typeList={states.typeList}
                operationSegment={states?.basicObj?.operationSegment}
                consignmentNo={consignmentNo}
              />
            ) : (
              <></>
            )}
            {
              // 特殊增值不需要展示
              states.basicObj.serviceType !== 'SS' && (
                <FpxTable2
                  dataSource={states.vasList}
                  columns={getForecastColumns()}
                  cellProps={dealCellProps}
                  hasBorder
                  className={styles.tableBox}
                >
                  {states.status === 'P' && (
                    <>
                      <div
                        slot="actualOperationQty"
                        data-cell={(value: string, index: number, record: any) => {
                          return (
                            <NumberPicker
                              min={0}
                              max={record?.operationQty}
                              value={value}
                              onChange={(v) => dealList(v, 'actualOperationQty', index)}
                            />
                          );
                        }}
                      />
                      <div
                        slot="actualLength"
                        data-cell={(value: string, index: number, record: any) => {
                          return (
                            <NumberPicker
                              min={0.1}
                              max={9999.9}
                              step={0.1}
                              value={value}
                              onChange={(v) => dealList(v, 'actualLength', index)}
                            />
                          );
                        }}
                      />
                      <div
                        slot="actualWidth"
                        data-cell={(value: string, index: number, record: any) => {
                          return (
                            <NumberPicker
                              min={0.1}
                              max={9999.9}
                              step={0.1}
                              value={value}
                              onChange={(v) => dealList(v, 'actualWidth', index)}
                            />
                          );
                        }}
                      />
                      <div
                        slot="actualHeight"
                        data-cell={(value: string, index: number, record: any) => {
                          return (
                            <NumberPicker
                              min={0.1}
                              max={9999.9}
                              step={0.1}
                              value={value}
                              onChange={(v) => dealList(v, 'actualHeight', index)}
                            />
                          );
                        }}
                      />
                      <div
                        slot="actualWeight"
                        data-cell={(value: string, index: number, record: any) => {
                          return (
                            <NumberPicker
                              min={0.01}
                              max={9999.99}
                              step={0.01}
                              value={value}
                              onChange={(v) => dealList(v, 'actualWeight', index)}
                            />
                          );
                        }}
                      />
                    </>
                  )}
                </FpxTable2>
              )
            }
          </>
        }
      />
      {/* 客户附件 */}
      <FpxPage
        tableTitle={$i18n.get({ id: 'CustomerAttachment.four_px', dm: '客户附件' })}
        table={
          <div className={styles.attachmentStyle} data-i18n-ignore-content>
            {states.customerAttachmentUrl?.map((e: string, i) => {
              const data = e.split('^');
              const url = data.length ? data[0] : '';
              const name = data.length ? data[1] : '';
              return (
                <Button
                  type="primary"
                  text
                  key={`attachment${i}`}
                  onClick={() => window.open(dealFileUrl(url || states.customerAttachmentUrl))}
                >
                  {name || states.customerAttachmentUrl}
                </Button>
              );
            })}
          </div>
        }
      />
      {/* 综合信息 */}
      {/* tab */}
      <FpxPage
        detail={
          <>
            <div className={styles.TableTitle}>
              {$i18n.get({ id: 'ComprehensiveInformation.four_px', dm: '综合信息' })}
            </div>
            <ul className={styles.attachmentStyle}>
              <li>{$i18n.get({ id: 'ResultAttachment.four_px', dm: '结果附件' })}</li>
              {states?.warehouseAttachmentUrl?.map((e) => {
                const url = e?.split('^')[0];
                // 校验URL是否合法,undefined或空字符串则不展示
                if (!url || url.trim() === '' || url.trim() === 'undefined') {
                  return null;
                }
                return (
                  <Button
                    type="primary"
                    text
                    key={`attachment${e}`}
                    onClick={() => {
                      window.open(dealFileUrl(url));
                    }}
                  >
                    {e?.split('^')[1]}
                  </Button>
                );
              })}
            </ul>
          </>
        }
        action={
          // 不等于特殊增值就展示
          states.basicObj?.serviceType !== 'SS' && (
            <ActionArea leftSection={<FpxTab dataSource={GENERAL_TAB} activeKey={states.tabKey} onChange={gettab} />} />
          )
        }
        table={
          <>
            <Box direction="row" justify="space-between" className={styles.showDetailLable} wrap>
              {!['T', 'P']?.includes(states.status) && states.basicObj?.operationSegment === 'OB' && getHLCom()}
              <span>
                {$i18n.get({ id: 'TotalWorkingHoursRequired.four_px', dm: '所需总工时' })}：
                {states.basicObj?.workingHour}
              </span>
              <span>
                {$i18n.get({ id: 'WarehouseRemarks.four_px', dm: '仓库备注' })}：{states.basicObj?.warehouseRemark}
              </span>
            </Box>
            {states.tabKey === 'res' && states.basicObj?.serviceType !== 'SS' ? (
              <FpxTable2
                dataSource={states.vasList}
                columns={getForecastColumns()}
                cellProps={dealCellProps}
                hasBorder
                className={styles.tableBox}
              />
            ) : (
              <FpxTable dataSource={states.logList} hasBorder={false} fixedHeader maxBodyHeight={600}>
                <Table.Column
                  title={$i18n.get({ id: 'Operator.four_px', dm: '操作人' })}
                  dataIndex={'operator'}
                  width={150}
                />
                <Table.Column
                  title={$i18n.get({ id: 'OperationTime.four_px', dm: '操作时间' })}
                  dataIndex={'createTime'}
                  width={150}
                />
                <Table.Column
                  title={$i18n.get({ id: 'MS.Description.four_px', dm: '描述' })}
                  dataIndex={'logType'}
                  width={200}
                  cell={(value: string) => vToLabel(VAS_OPERATE_LOG, value)}
                />
                <Table.Column title={$i18n.get({ id: 'Remarks', dm: '备注' })} dataIndex={'remark'} width={200} />
              </FpxTable>
            )}
            <AssociatedOrderCom
              consignmentNo={states.basicObj?.consignmentNo}
              relativeData={states.relativeData}
              uptRef={uptRef}
              callBack={uptCallBack}
            />
            <VasMatchInventory
              basicObj={states.basicObj}
              vasInventoryList={states.vasInventoryList}
              visible={states.visible}
              handleVisible={(v: boolean) => setStates({ visible: v })}
            />
          </>
        }
      />
    </>
  );
};

export default VASDetail;
